/**
 * <s-wordmark> — swaps every written "DISTVNT" (any capitalization) inside it
 * for the DISTVNT logo, sized to the cap height of the surrounding text and
 * tinted with the text's own color (so it follows dark mode and dark sections).
 * Words inside links like distvnt.com, @distvnt, or file names are left alone.
 *
 * Required markup:
 *   <s-wordmark class="contents" data-src="<logo url>" data-ratio="<width / height>" data-label="DISTVNT">…</s-wordmark>
 *   Add data-no-wordmark to any element whose text should stay as text.
 */
const PATTERN = /(?<![\w.@/-])distvnt(?![\w@/-]|\.\w)/gi;
const SKIP = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'INPUT', 'SELECT', 'OPTION', 'TITLE', 'CODE', 'PRE', 'svg', 'SVG', 'TEMPLATE']);

class SWordmark extends HTMLElement {
  connectedCallback() {
    this.src = this.dataset.src;
    this.ratio = parseFloat(this.dataset.ratio) || 5.5;
    this.label = this.dataset.label || 'DISTVNT';
    if (!this.src) return;
    this.scan(this);
    this.observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === 'characterData') {
          this.scan(record.target);
        } else {
          record.addedNodes.forEach((node) => this.scan(node));
        }
      }
    });
    this.observer.observe(this, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  skipped(element) {
    for (let el = element; el && el !== this; el = el.parentElement) {
      if (SKIP.has(el.tagName) || el.isContentEditable || el.hasAttribute('data-no-wordmark') || el.hasAttribute('data-wordmark')) return true;
    }
    return false;
  }

  scan(root) {
    if (root.nodeType === Node.TEXT_NODE) {
      this.replace(root);
      return;
    }
    if (root.nodeType !== Node.ELEMENT_NODE) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const matches = [];
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      PATTERN.lastIndex = 0;
      if (PATTERN.test(node.nodeValue)) matches.push(node);
    }
    matches.forEach((node) => this.replace(node));
  }

  replace(node) {
    const text = node.nodeValue;
    if (!text || !node.parentElement || this.skipped(node.parentElement)) return;
    PATTERN.lastIndex = 0;
    if (!PATTERN.test(text)) return;
    PATTERN.lastIndex = 0;

    const fragment = document.createDocumentFragment();
    let last = 0;
    for (const match of text.matchAll(PATTERN)) {
      if (match.index > last) fragment.append(text.slice(last, match.index));
      fragment.append(this.mark());
      last = match.index + match[0].length;
    }
    if (last < text.length) fragment.append(text.slice(last));
    node.replaceWith(fragment);
  }

  mark() {
    const mark = document.createElement('span');
    mark.setAttribute('role', 'img');
    mark.setAttribute('aria-label', this.label);
    mark.setAttribute('data-wordmark', '');
    const height = '0.74em';
    const mask = `url("${this.src}") center / contain no-repeat`;
    Object.assign(mark.style, {
      display: 'inline-block',
      height,
      width: `calc(${height} * ${this.ratio})`,
      verticalAlign: 'baseline',
      backgroundColor: 'currentColor',
      mask,
      webkitMask: mask,
    });
    return mark;
  }
}

if (!customElements.get('s-wordmark')) customElements.define('s-wordmark', SWordmark);
